import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useColorMode, Box, Spinner } from '@chakra-ui/react';
import { FaPlus, FaEdit, FaTrash, FaBookOpen } from 'react-icons/fa';
import { motion } from 'framer-motion';
import JournalRender from './JournalRender';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

/* ── Miniature de couverture — rendu réel du journal à l'échelle ── */
function JournalCover({ journal }) {
  const layout      = journal.layoutData || {};
  const accentColor = layout.accentColor || '#274559';
  const columnCount = layout.columnCount || 2;
  const blocks      = layout.blocks || [];

  // On ne montre que les blocs de la page 1 (avant le page-break)
  const breakIdx   = blocks.findIndex(b => b.type === 'page-break');
  const coverBlocks = breakIdx >= 0 ? blocks.slice(0, breakIdx) : blocks;

  const COVER_W   = 660;   // largeur interne du rendu
  const SCALE     = 0.36;  // facteur d'échelle
  const OUTER_W   = Math.round(COVER_W * SCALE);   // ~238px
  const INNER_H   = 860;   // hauteur interne à capturer
  const OUTER_H   = Math.round(INNER_H * SCALE);   // ~310px

  return (
    <div style={{ width: OUTER_W, height: OUTER_H, overflow: 'hidden', position: 'relative', borderRadius: '3px', flexShrink: 0 }}>
      {/* Fade en bas pour donner l'impression de "coupure" naturelle */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60px', background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))', zIndex: 2, pointerEvents: 'none' }} />

      <div style={{
        width: COVER_W,
        maxHeight: INNER_H,
        overflow: 'hidden',
        transform: `scale(${SCALE})`,
        transformOrigin: 'top left',
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
        <JournalRender
          title={journal.title}
          period={journal.period}
          excerpt={journal.excerpt}
          accentColor={accentColor}
          columnCount={columnCount}
          blocks={coverBlocks}
          page1Settings={layout.page1Settings}
          hashtag={layout.hashtag}
          issueLabel={layout.issueLabel}
          runningLabel={layout.runningLabel}
          publishedAt={journal.createdAt}
        />
      </div>
    </div>
  );
}

/* ── Carte kiosque ── */
function JournalCard({ journal, isAdmin, onDelete, index }) {
  const navigate = useNavigate();

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer "${journal.title}" ?`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/journal/${journal.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onDelete(journal.id);
    } catch { alert('Erreur lors de la suppression.'); }
  };

  const layout      = journal.layoutData || {};
  const accentColor = layout.accentColor || '#274559';

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
      onClick={() => navigate(`/journal/${journal.id}`)}
    >
      {/* Couverture */}
      <div style={{ position: 'relative', boxShadow: '4px 6px 20px rgba(0,0,0,0.22)', borderRadius: '4px', overflow: 'hidden', transition: 'transform 0.25s, box-shadow 0.25s' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-6px) rotate(-0.5deg)'; e.currentTarget.style.boxShadow = '8px 16px 32px rgba(0,0,0,0.28)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '4px 6px 20px rgba(0,0,0,0.22)'; }}
      >
        <JournalCover journal={journal} />

        {/* Boutons admin superposés */}
        {isAdmin && (
          <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px', zIndex: 10 }}>
            <button onClick={() => navigate(`/modifier-journal/${journal.id}`)}
              style={{ padding: '5px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.92)', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#d97706', fontWeight: 700, backdropFilter: 'blur(4px)' }}>
              <FaEdit size={11} />
            </button>
            <button onClick={handleDelete}
              style={{ padding: '5px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.92)', border: 'none', cursor: 'pointer', fontSize: '11px', color: '#ef4444', fontWeight: 700, backdropFilter: 'blur(4px)' }}>
              <FaTrash size={11} />
            </button>
          </div>
        )}

        {/* Tranche latérale (effet livre) */}
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '6px', background: `linear-gradient(to left, ${accentColor}aa, transparent)`, pointerEvents: 'none' }} />
      </div>

      {/* Infos sous la couverture */}
      <div style={{ marginTop: '10px', paddingLeft: '2px' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111', lineHeight: 1.3, marginBottom: '3px' }}>
          {journal.title}
        </div>
        <div style={{ fontSize: '0.65rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {formatDate(journal.createdAt)}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Page principale ── */
export default function Journal() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const navigate = useNavigate();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const ITEMS_PER_PAGE          = 9;

  const isAdmin = !!localStorage.getItem('token');

  const bg      = isDark ? '#0a0f16' : '#f0f4f8';
  const textPrimary = isDark ? '#f1f5f9' : '#0f172a';
  const textMuted   = isDark ? '#94a3b8' : '#64748b';

  useEffect(() => {
    axios.get(`${API_URL}/api/journal`)
      .then(res => {
        // On récupère le layoutData pour chaque journal
        Promise.all(res.data.map(j =>
          axios.get(`${API_URL}/api/journal/${j.id}`).then(r => r.data).catch(() => j)
        )).then(full => setJournals(full));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = (id) => setJournals(prev => prev.filter(j => j.id !== id));

  if (loading) return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg={bg}>
      <Spinner size="xl" thickness="4px" color="blue.500" />
    </Box>
  );

  return (
    <Box minH="100vh" bg={bg} pt="80px">
      <Helmet>
        <title>Journal HIS | Hetep Iaout Services</title>
        <meta name="description" content="Retrouvez le journal trimestriel de HIS." />
      </Helmet>

      {/* Hero */}
      <div style={{ textAlign: 'center', padding: '48px 16px 40px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 14px', borderRadius: '999px', background: isDark ? '#1e293b' : '#e0f2fe', color: isDark ? '#7dd3fc' : '#0369a1', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px' }}>
            <FaBookOpen size={10} /> Publication trimestrielle
          </div>
          <h1 style={{ margin: '0 0 12px', fontSize: '2.8rem', fontWeight: 900, color: textPrimary, fontFamily: 'Georgia, serif' }}>
            Journal <span style={{ color: '#2563eb' }}>HIS</span>
          </h1>
          <p style={{ margin: '0 auto', maxWidth: '480px', fontSize: '1rem', color: textMuted, lineHeight: 1.65 }}>
            Chaque trimestre, notre journal vous partage l'actualité, les engagements et la vie de Hetep Iaout Services.
          </p>
          {isAdmin && (
            <button onClick={() => navigate('/ajouter-journal')}
              style={{ marginTop: '24px', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', borderRadius: '12px', background: '#2563eb', color: '#fff', fontWeight: 700, fontSize: '14px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(37,99,235,0.35)' }}>
              <FaPlus size={12} /> Publier un nouveau journal
            </button>
          )}
        </motion.div>
      </div>

      {/* Grille kiosque */}
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 24px 80px' }}>
        {journals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: textMuted }}>
            <FaBookOpen size={48} style={{ margin: '0 auto 16px', opacity: 0.2 }} />
            <p style={{ fontWeight: 600, fontSize: '1.1rem' }}>Aucun journal publié pour l'instant.</p>
          </div>
        ) : (() => {
          const totalPages = Math.ceil(journals.length / ITEMS_PER_PAGE);
          const paginated = journals.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
          return (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px 32px', justifyContent: 'center' }}>
                {paginated.map((journal, i) => (
                  <JournalCard
                    key={journal.id}
                    journal={journal}
                    isAdmin={isAdmin}
                    onDelete={handleDelete}
                    index={i}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '48px' }}>
                  <button
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={page === 1}
                    style={{ padding: '8px 18px', borderRadius: '10px', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: '13px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.3 : 1 }}
                  >
                    ← Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      style={{ width: '36px', height: '36px', borderRadius: '10px', border: n === page ? 'none' : `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, background: n === page ? '#2563eb' : 'transparent', color: n === page ? 'white' : isDark ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={page === totalPages}
                    style={{ padding: '8px 18px', borderRadius: '10px', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, background: 'transparent', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, fontSize: '13px', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.3 : 1 }}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </Box>
  );
}
